// Lib
import React, { ReactElement, ReactNode, RefObject } from 'react';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import { compose } from '../../node_module_clones/recompose';

// Measurements store
import measurementsConnect from '../components/measurementsStore/measurementsConnect';
import { getCanvasElementMeasurementsBoundingRect } from '../components/measurementsStore/elementMeasurements/elementMeasurementsSelector';

// Utils
import { addZoomMarginsToCanvasElementsBoundingRect } from './zoom/zoomScaleUtils';
import { getScrollableAreaSizeFromContentRect } from './zoom/zoomScrollAreaSizeUtils';

// Selectors
import { getScrollAreaSizeAsObject } from './store/scrollAreaSizeSelector';

// Type
import { Point } from '../../common/maths/geometry/pointTypes';
import { Rect, RectSize } from '../../common/maths/geometry/rect/rectTypes';

// Constants
import { ZOOM_MODES } from './zoom/zoomConstants';

interface CanvasScrollAreaProps {
    canvasElementsBoundingRect: Rect;
    canvasSize: RectSize;
    savedScrollAreaSize: RectSize | null;
    scrollAreaRef: RefObject<HTMLDivElement>;
    zoomTranslationPx: Point;
    children: ReactNode;
    zoomScale: number;
    zoomMode: string;
}

/**
 * If the user does not have zoom enabled or is not zoomed out, the scroll area size will equal canvas size.
 *
 * Otherwise - If the user is zoomed out the content bounding rectangle, along with a margin will be used.
 *
 * NOTE: The scroll area has a CSS property of 100% min width and height, so even if the calculated scroll
 *    area is less than the viewport width, it'll still be sized large enough for the viewport.
 */
const calculateRenderedScrollAreaSize = (props: CanvasScrollAreaProps): RectSize => {
    const { zoomMode, zoomScale, zoomTranslationPx, canvasSize, savedScrollAreaSize, canvasElementsBoundingRect } =
        props;

    if (zoomMode === ZOOM_MODES.ZOOM_TO_FIT && savedScrollAreaSize) return savedScrollAreaSize;

    // Only limit the scroll area size if zoomed out
    let targetRect = canvasSize;

    if (zoomScale < 1 && canvasElementsBoundingRect?.width && canvasElementsBoundingRect?.height) {
        targetRect = addZoomMarginsToCanvasElementsBoundingRect(canvasElementsBoundingRect);
    }

    const scrollAreaSize = getScrollableAreaSizeFromContentRect(targetRect, zoomScale, zoomTranslationPx);

    // If the scroll area was larger than it is calculated to be now (e.g. elements were moved when we were zoomed out)
    //  then don't shrink it, otherwise this can result in a visual jump to the user
    if (savedScrollAreaSize) {
        scrollAreaSize.width = Math.ceil(Math.max(scrollAreaSize.width, savedScrollAreaSize.width));
        scrollAreaSize.height = Math.ceil(Math.max(scrollAreaSize.height, savedScrollAreaSize.height));
    }

    return scrollAreaSize;
};

const mapMeasurementsStateToProps = createStructuredSelector({
    canvasElementsBoundingRect: getCanvasElementMeasurementsBoundingRect,
});

const mapStateToProps = createStructuredSelector({
    savedScrollAreaSize: getScrollAreaSizeAsObject,
});

const CanvasScrollArea = (props: CanvasScrollAreaProps): ReactElement => {
    const { children, scrollAreaRef } = props;

    const renderedScrollAreaSize = calculateRenderedScrollAreaSize(props);

    return (
        <div
            className="CanvasScrollArea"
            ref={scrollAreaRef}
            style={renderedScrollAreaSize}
            // Long press delay on iPads
            data-long-press-delay="500"
        >
            {children}
        </div>
    );
};

const enhance = compose(measurementsConnect(mapMeasurementsStateToProps), connect(mapStateToProps));

export default enhance(CanvasScrollArea);
